import { useTranslations } from "use-intl";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { receivingAccountFieldView, type ReceivingAccountOption, type Seller } from "@/services/invoiceGeneration";

/**
 * Create Invoice's "Paid into" field (FIN-283, Draft A). The rules live in packages/invoicing's
 * receivingAccountFieldView; see specs/receiving-bank-account/screens.md.
 */
export function ReceivingAccountField({ seller, value, onChange, notice }: {
  seller: Seller | null;
  /** The invoice's own choice; null means the company default. */
  value: string | null;
  onChange: (bankAccountId: string) => void;
  /** Shown after a pick changed the payment method (EC5) or a copy lost its account (EC8). */
  notice?: string | null;
}) {
  const t = useTranslations("consultantInvoiceCreate.paidInto");
  const view = receivingAccountFieldView(seller, value);
  if (view.kind === "hidden") return null;
  return (
    <div>
      <Label className="mb-1.5 block" id="paid-into-label" htmlFor={view.kind === "select" ? "paid-into" : undefined}>{t("label")}</Label>
      {view.kind === "readonly" ? (
        <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm" aria-labelledby="paid-into-label" data-testid="paid-into-single">
          <OptionText option={view.option} />
        </p>
      ) : (
        <>
          <Select value={view.selectedId} onValueChange={onChange}>
            <SelectTrigger id="paid-into" aria-label={t("label")} aria-invalid={view.alert ? true : undefined} className={view.alert ? "border-destructive" : undefined}>
              <SelectValue placeholder={t("placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {view.options.map((o) => <SelectItem key={o.id} value={o.id}><OptionText option={o} /></SelectItem>)}
            </SelectContent>
          </Select>
          {view.hint && <p className="mt-1 text-xs text-muted-foreground">{t("choiceHint")}</p>}
          {view.alert && <p className="mt-1 text-xs text-destructive" role="alert">{t("unavailableAlert")}</p>}
        </>
      )}
      {notice && <p className="mt-1 rounded-md bg-warning-weak px-2 py-1.5 text-xs text-warning-foreground" role="status">{notice}</p>}
    </div>
  );
}

function OptionText({ option }: { option: ReceivingAccountOption }) {
  const t = useTranslations("consultantInvoiceCreate.paidInto");
  return <>{option.text}{option.isPrimary && <Badge variant="secondary" className="ml-1.5">{t("primary")}</Badge>}</>;
}
